import { API_CONFIG } from "../config";

/**
 * Types of images that can be managed by the image service
 */
export type ImageType =
  | 'champignon'
  | 'espece'
  | 'arbre'
  | 'epaysage'
  | 'essence'
  | 'avatar'
  | 'logo'
  | 'default';

/**
 * Configuration object for image options by type
 */
interface ImageConfig {
  path: string;
  placeholder: string;
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
}

/**
 * Default image configuration by type
 */
const IMAGE_CONFIG: Record<ImageType, ImageConfig> = {
  champignon: {
    path: '/images/champignons',
    placeholder: '/images/placeholders/champignon-placeholder.svg',
    maxWidth: 800,
    maxHeight: 600,
  },
  espece: {
    path: '/images/especes',
    placeholder: '/images/placeholders/espece-placeholder.svg',
    maxWidth: 800,
    maxHeight: 600,
  },
  arbre: {
    path: '/images/arbres',
    placeholder: '/images/placeholders/arbre-placeholder.svg',
    maxWidth: 1200,
    maxHeight: 800,
  },
  epaysage: {
    path: '/images/epaysages',
    placeholder: '/images/placeholders/arbre-placeholder.svg',
    maxWidth: 1200,
    maxHeight: 800,
  },
  essence: {
    path: '/images/essences',
    placeholder: '/images/placeholders/arbre-placeholder.svg',
    maxWidth: 1200,
    maxHeight: 800,
  },
  avatar: {
    path: '/images',
    placeholder: '/images/placeholders/avatar-placeholder.svg',
    maxWidth: 300,
    maxHeight: 300,
    quality: 90,
  },
  logo: {
    path: '/images/logos',
    placeholder: '/images/placeholders/logo-placeholder.svg',
    maxWidth: 400,
    maxHeight: 400,
    quality: 95,
  },
  default: {
    path: '/uploads',
    placeholder: '/images/placeholders/default-placeholder.svg',
  },
};

/**
 * A service to manage image URLs by type
 */
export class ImageService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_CONFIG.baseURL;
  }

  /**
   * Get the full URL for an image
   * 
   * @param filename The filename or partial path of the image
   * @param type The type of image (champignon, espece, etc.)
   * @returns The full URL to the image
   */
  getImageUrl(filename: string | null | undefined, type: ImageType = 'default'): string {
    if (!filename) {
      return this.getPlaceholderUrl(type);
    }

    // If filename already contains a full URL, return it
    if (filename?.startsWith('http://') || filename?.startsWith('https://')) {
      return filename;
    }

    // If filename already contains the base URL, return it
    if (filename?.startsWith(this.baseUrl)) {
      return filename;
    }

    // If filename already starts with a path that matches our path config, use direct URL
    if (filename?.startsWith('/uploads/')) {
      return `${this.baseUrl}${filename}`;
    }

    // Use the API image controller for better control
    return `${this.baseUrl}/api/v1${IMAGE_CONFIG[type].path}/${filename}`;
  }

  /**
   * Get the URL for a placeholder image by type
   * 
   * @param type The type of image
   * @returns The URL to the placeholder image
   */
  getPlaceholderUrl(type: ImageType = 'default'): string {
    return `${this.baseUrl}${IMAGE_CONFIG[type].placeholder}`;
  }

  /**
   * Transform an image object with multiple images to a clean array of URLs
   * 
   * @param imgObj The image object (like {img1: 'url1', img2: 'url2'})
   * @param type The type of images
   * @returns Array of full image URLs
   */
  getImagesFromObject(
    imgObj: Record<string, string> | string | null | undefined,
    type: ImageType = 'default'
  ): string[] {
    if (!imgObj) {
      return [this.getPlaceholderUrl(type)];
    }

    if (typeof imgObj === 'string') {
      return [this.getImageUrl(imgObj, type)];
    }

    return Object.values(imgObj)
      .filter(Boolean)
      .map(url => this.getImageUrl(url, type));
  }

  /**
   * Get the primary image from an image object or string
   * 
   * @param imgObj The image object or string
   * @param type The type of image
   * @returns The URL to the primary image
   */
  getPrimaryImage(
    imgObj: Record<string, string> | string | null | undefined,
    type: ImageType = 'default'
  ): string {
    if (!imgObj) {
      return this.getPlaceholderUrl(type);
    }

    if (typeof imgObj === 'string') {
      return this.getImageUrl(imgObj, type);
    }

    // Try to get img1 first, then fallback to any available image
    const primaryImg = imgObj.img1 || Object.values(imgObj).find(Boolean);
    return primaryImg ? this.getImageUrl(primaryImg, type) : this.getPlaceholderUrl(type);
  }

  /**
   * Get an optimized image URL with specific dimensions
   * 
   * @param filename The image filename
   * @param type The type of image
   * @param width Optional requested width
   * @param height Optional requested height
   * @param bustCache Optional flag to add cache-busting parameter
   * @returns The URL to the optimized image
   */
  getOptimizedImageUrl(
    filename: string | null | undefined,
    type: ImageType = 'default',
    width?: number,
    height?: number,
    bustCache: boolean = false
  ): string {
    if (!filename) {
      return this.getPlaceholderUrl(type);
    }

    const baseUrl = this.getImageUrl(filename, type);

    // If width and height are not provided, use the defaults from the config
    const config = IMAGE_CONFIG[type];
    const w = width || config.maxWidth;
    const h = height || config.maxHeight;

    // Build query parameters
    const params = new URLSearchParams();
    if (w) params.set('w', w.toString());
    if (h) params.set('h', h.toString());
    params.set('q', (config.quality || 80).toString());

    // Add cache-busting parameter if requested
    if (bustCache) {
      params.set('_t', Date.now().toString());
    }

    // If there's no width or height to optimize for, return the base URL with cache-busting if needed
    if (!w && !h) {
      return bustCache ? `${baseUrl}?${params.toString()}` : baseUrl;
    }

    return `${baseUrl}?${params.toString()}`;
  }

  /**
   * Add cache-busting parameter to an image URL
   * 
   * @param url The image URL
   * @returns The URL with cache-busting parameter
   */
  addCacheBusting(url: string): string {
    if (!url) return url;

    try {
      // Check if url is already a full URL
      let urlObj: URL;
      if (url.startsWith('http://') || url.startsWith('https://')) {
        urlObj = new URL(url);
      } else {
        urlObj = new URL(url, this.baseUrl);
      }

      // Add or update cache-busting parameter
      urlObj.searchParams.set('_t', Date.now().toString());
      return urlObj.toString();
    } catch {
      // If URL parsing fails, return as is
      return url;
    }
  }
}

// Create and export a singleton instance
export const imageService = new ImageService();
